import { autoinject, transient } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { activationStrategy } from "aurelia-router";
import * as Enumerable from "linq";
import { MepService } from "../../../../../../services/mep/mep.service";
import {
    MepDashboardRequest, MepDashboardSummaryResponse, MepDashboardTotalEmissions, 
    MepPortDetails, BaseMepFilterablePage, EmissionTypeCategoryEnum
} from "../../../../../../models/mep/mep.models";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { NotificationType, NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { MepVesselStatistics } from "./partials/mep-vessel-statistics/mep-vessel-statistics";
import { MepEmissionsByMonthViewModel } from "./partials/mep-emissions-by-month/mep-emissions-by-month";
import { EmissionsByTypeTabEnum, EmissionsByTypeTabViewModel } from "./partials/mep-emissions-by-type/mep-emissions-by-type";
import { MepLookupService } from "../../../../../../services/mep/mep-lookup.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { MepDashboardFilter } from "./partials/mep-dashboard-filter/mep-dashboard-filter";
import { CustomerDetailsModel } from "../../../../../../models/customers/customer.models";
import { MepTopContributors } from "./partials/mep-top-contributors/mep-top-contributors";
import { MepSingleEmissionType } from "./partials/mep-single-emission-type/mep-single-emission-type";
import { EventAggregator } from "aurelia-event-aggregator";
import { EmissionsByCargoTypeTabViewModel } from "./partials/mep-emissions-by-cargo-type/mep-emissions-by-cargo-type";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
@transient()
@useView('views/pages/modules/mep/tabs/mep-dashboard/mep-dashboard.html')
export class MepDashboard extends BaseMepFilterablePage<MepDashboardFilter> {

    public portDetails: MepPortDetails;
    public params: any;
    vesselStatisticsViewModel: MepVesselStatistics;
    topContributorsViewModel: MepTopContributors;
    emissionsByTypeTabViewModels: any[];
    selectedTab: any;
    activeEmissionsByTypeTabViewModels: any[];
    emissionsByMonthViewModel: MepEmissionsByMonthViewModel;
    singleEmissionTypeViewModel: MepSingleEmissionType;
    supportsCargoType: boolean;

    portId: number;
    mepSummary: MepDashboardSummaryResponse;
    isMepSummaryLoading: boolean = false;

    currentTab: any;

    refreshDataFunc: (request: MepDashboardRequest) => Promise<string[]>;
    getRequestDataFunc: () => MepDashboardRequest;
    emissionsPerPortActivityPerHour: number;

    //Claims
    canGeneratePortDashboardReport: boolean;

    customerDetails: CustomerDetailsModel;

    constructor(protected dialogClientService: DialogClientService, private userClientService: UserClientService,
        private mepService: MepService, private notificationClientService: NotificationClientService, private eventAggregator: EventAggregator,
        private mepLookupService: MepLookupService, userClaimsService: UserClaimsService, private configService: ConfigService) {
        super();
        this.refreshDataFunc = (request: MepDashboardRequest) => this.reloadSummaryReportData(request);
        this.getRequestDataFunc = () => this.filterViewModel.getLastRequest();
        this.canGeneratePortDashboardReport = userClaimsService.hasPermission(Claims.CanGeneratePortDashboardReport);
    }

    activate(param) {
        this.portId = param.portId;
        this.supportsCargoType = param.supportsCargoType;
        if (!this.filterViewModel)
            this.initializeFilterVM();
        this.singleEmissionTypeViewModel = new MepSingleEmissionType(this.mepService, this.filterViewModel);
        this.vesselStatisticsViewModel = new MepVesselStatistics(this.getRequestDataFunc, this.dialogClientService, this.mepService, this.notificationClientService, this.eventAggregator, this.filterViewModel);
        this.topContributorsViewModel = new MepTopContributors(this.mepService, this.eventAggregator, Number(param.portId), this.filterViewModel);
        this.emissionsByTypeTabViewModels = [
            new EmissionsByTypeTabViewModel(EmissionsByTypeTabEnum.PointOfInterest, this.filterViewModel, this.mepService, this.notificationClientService, this.portId, this.configService),
            new EmissionsByTypeTabViewModel(EmissionsByTypeTabEnum.VesselType, this.filterViewModel, this.mepService, this.notificationClientService, this.portId, this.configService),
            new EmissionsByCargoTypeTabViewModel(this.filterViewModel, this.mepService, this.portId, this.supportsCargoType),
            new EmissionsByTypeTabViewModel(EmissionsByTypeTabEnum.OperationMode, this.filterViewModel, this.mepService, this.notificationClientService, this.portId, this.configService)
        ];

        this.customerDetails = CustomerDetailsModel.getCustomerDetailModelFromRSUser(window["rightshipUser"]);

        this.emissionsByMonthViewModel = new MepEmissionsByMonthViewModel(this.filterViewModel, this.mepService, this.customerDetails && this.customerDetails.isMepTrial, this.configService);
        this.topContributorsViewModel.isMepTrial = this.customerDetails && this.customerDetails.isMepTrial;
        this.params = param;
    }

    initializeFilterVM(model?): MepDashboardFilter {
        this.filterViewModel = new MepDashboardFilter(model && model.portId ? model.portId : this.portId, this.supportsCargoType, this.refreshDataFunc, this.notificationClientService, this.mepLookupService);

        return this.filterViewModel;
    }

    attached() {
        this.filterViewModel.initLookups().then(() => {
            this.filterViewModel.supportsCargoType = this.params.supportsCargoType;
            this.filterViewModel.applyAllFilters();

            this.mepService.getMepPortDetails(this.portId, this.filterViewModel?.selectedYear, true).then(x => {
                this.portDetails = x;
            }).catch(() => {
                this.notificationClientService.show("MEP Error", "Error loading port details!", NotificationType.Error);
            });
        }).catch(() => {
            this.notificationClientService.show("MEP Error", "Error loading year!", NotificationType.Error);
        });
    }

    generateReport() {
        if (!this.isMepSummaryLoading &&
            !this.vesselStatisticsViewModel.isVesselStatisticsLoading &&
            !this.topContributorsViewModel.isTopContributorsLoading &&
            !this.emissionsByMonthViewModel.isEmissionsByMonthLoading &&
            !this.singleEmissionTypeViewModel.loadingAdditionalSingleEmissionData &&
            !this.activeEmissionsByTypeTabViewModels.every(x => x.isEmissionsByTypeLoading || x.isHeatmapLoading) &&
            this.vesselStatisticsViewModel.vesselStatisticsItems &&
            this.emissionsByMonthViewModel.emissionsChartViewModel) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PrintMepDashboardDialog, this, true, false, true);
        }
        else {
            this.notificationClientService.show("Page is loading, please try again once its loaded", "", NotificationType.Info);
        }
    }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    public getTitle() {
        return 'Welcome ' + this.userClientService.currentUser.userName;
    }

    public getEmissionTypeCategoryTotal(emissionTypeCategoryId: number) {
        if(this.mepSummary) {
            const emissionsSet = Enumerable.from(this.mepSummary.totalEmissionsPerType)
                .where(x => x.emissionTypeCategoryId == emissionTypeCategoryId);

            if (!(this.mepSummary && this.mepSummary.totalEmissionsPerType)) {
                return 0;
            }

            if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
                return emissionsSet.sum(x => x.totalEmissionsQtyTonnes * x.weightingFactor);
            } else {
                return emissionsSet.sum(x => x.totalEmissionsQtyTonnes);
            }
        }
    }

    public getEmissionTypeCategoryClass(emissionTypeCategoryId: number) {
        if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
            return "total-ghg-emissions";
        } else {
            return "total-criteria-pollutants";
        }
    }

    public getEmissionTypeCategoryUnit(emissionTypeCategoryId: number) {
        if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
            return "tCO<sub>2</sub>e_100yr";
        } else {
            return "tonnes";
        }
    }

    public getEmissionTypeCategoryTitle(emissionTypeCategoryId: number) {
        if (emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions) {
            return "Total GHG Emissions";
        } else {
            return "Total Criteria Pollutants";
        }
    }

    public getTotalEmissionsPerType(emissionTypeCategoryId: number) {
        return this.mepSummary && this.mepSummary.totalEmissionsPerType
            ? this.mepSummary.totalEmissionsPerType.filter(x => x.emissionTypeCategoryId == emissionTypeCategoryId)
            : [];
    }

    public getTotalEmissionsByScopePerType(emissionTypeCategoryId: number) {
        return this.mepSummary && this.mepSummary.totalEmissionsByScope
            ? this.mepSummary.totalEmissionsByScope.filter(x => x.emissionTypeCategoryId == emissionTypeCategoryId)
            : [];
    }

    reloadSummaryReportData(request: MepDashboardRequest): Promise<string[]> {
        this.clearDashboardData();
        const promises = [];
        const dashboardPromise = this.getDashboardSummary(request);
        promises.push(dashboardPromise);
        promises.push(this.vesselStatisticsViewModel.getMepVesselStatistics(request));
        promises.push(this.topContributorsViewModel.getTopVesselsEmissionsPerformers(request));
        promises.push(this.emissionsByMonthViewModel.getEmissionsByMonth(request));
        const p1 = this.refreshDataForTabs(request);
        if (p1) promises.push(p1);
        const p2 = this.singleEmissionTypeViewModel.refreshData(dashboardPromise, request);
        if (p2) promises.push(p2);
        return Promise.all(promises);
    }

    clearDashboardData() {
        this.mepSummary = null;
        this.vesselStatisticsViewModel.clearData();
        this.topContributorsViewModel.clearData();
        this.emissionsByMonthViewModel.clearData();
        this.emissionsPerPortActivityPerHour = null;
    }

    getDashboardSummary(request: MepDashboardRequest) {
        return new Promise<MepDashboardSummaryResponse>((resolve, reject) => {
            this.isMepSummaryLoading = true;
            this.mepService.getMepDashboardSummaryForPortDashboard(request).then(response => {
                this.isMepSummaryLoading = false;
                if (!response.hasError) {
                    this.mepSummary = response;
                    resolve(response);
                } else {
                    reject("MEP Dashboard Summary - Error retrieving the dashboard summary!");
                }

            }).catch(() => {
                this.isMepSummaryLoading = false;
                reject("MEP Dashboard Summary - Error retrieving the dashboard summary!");
            });
        });
    }

    tabChanged() {
        if (!this.selectedTab) //we want to make this check to ensure that if selected tab was not loaded yet we don't call refresh data twice
            return;

        this.refreshDataForTabs(this.filterViewModel.getLastRequest());
    }

    refreshDataForTabs(request: MepDashboardRequest): Promise<string> {
        this.activeEmissionsByTypeTabViewModels = Enumerable.from(this.emissionsByTypeTabViewModels).where(x => x.isVisible).toArray();

        const hasRequestChanged = this.filterViewModel.hasRequestChanged;
        //we only want to update the data if the tab changed 
        if (this.selectedTab != this.currentTab || hasRequestChanged) {
            this.selectedTab = this.currentTab ? Enumerable.from(this.activeEmissionsByTypeTabViewModels)
                .where(x => x.emissionsTabType == this.currentTab.emissionsTabType).firstOrDefault() : null;

            if (!this.selectedTab) {
                this.selectedTab = this.activeEmissionsByTypeTabViewModels ? Enumerable.from(this.activeEmissionsByTypeTabViewModels).firstOrDefault() : null;
                this.currentTab = this.selectedTab;
            }

            if (this.selectedTab) {
                this.selectedTab.isMepTrial = this.customerDetails && this.customerDetails.isMepTrial;
            }

            if (this.selectedTab && request) {
                this.emissionsByTypeTabViewModels.forEach(tab => {
                    tab.clearEmissionsData();
                });
                return this.selectedTab.getTabData(request);
            }
        }
        return null;
    }

    applyEmissionFormulaFilter(emissionType: MepDashboardTotalEmissions) {
        if (emissionType) {
            this.filterViewModel.changeEmissionTypesFilter([emissionType.emissionsTypeId]);
            this.filterViewModel.applyAllFilters();
        }
    }

    refresh(data?: { portId: number, year: number, supportsCargoType: boolean }) {
        if (this.portDetails) {
            this.filterViewModel.clearAllFilters();
            if (data && data.portId) {
                this.portId = data.portId;
                this.filterViewModel.portId = data.portId;
                this.topContributorsViewModel.portId = data.portId;
                this.filterViewModel.supportsCargoType = data.supportsCargoType;
                this.emissionsByTypeTabViewModels.forEach(x => {
                    x.portId = data.portId;
                    x.supportsCargoType = this.filterViewModel.supportsCargoType;
                })
            }

            if (data && data.year) {
                this.filterViewModel.selectedYear = data.year;
            }

            this.filterViewModel.initLookups().then(() => {
                //Set loading at this stage as otherwise old data will appear while loading port details data
                this.topContributorsViewModel.isTopContributorsLoading = true;
                this.vesselStatisticsViewModel.isVesselStatisticsLoading = true;
                this.emissionsByMonthViewModel.isEmissionsByMonthLoading = true;
                if (this.selectedTab.showMap) {
                    this.selectedTab.isHeatmapLoading = true;
                } else {
                    this.selectedTab.isEmissionsByTypeLoading = true;
                }
                this.isMepSummaryLoading = true;

                this.mepService.getMepPortDetails(this.portId, this.filterViewModel?.selectedYear, true).then(x => {
                    this.portDetails = x;
                    this.filterViewModel.applyAllFilters();
                }).catch(() => {
                    this.notificationClientService.show("MEP Error", "Error loading port details!", NotificationType.Error);
                });
            })
        }
    }
}
